import { ASSET_TYPES, isDecimalString } from '@porttang/core';
import type { AssetType, RealizedEntry, ValidatedDividend } from '@porttang/core';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  type DocumentData,
} from 'firebase/firestore';
import { firestore } from '../../shared/firebase/client';

/** `users/{uid}/realized` — spec Section 4.8 */
export function realizedCollection(uid: string) {
  return collection(firestore, 'users', uid, 'realized');
}

/**
 * แปลงข้อมูลดิบจาก Firestore เป็น RealizedEntry
 *
 * throw เมื่อเจอเอกสารที่ผิดรูป ไม่ข้ามเงียบ ๆ — เหตุผลเดียวกับ `toHolding`:
 * รายการที่หายไปเงียบ ๆ คือกำไรที่หายจากยอดรวมโดยไม่มีใครรู้
 */
function toRealizedEntry(id: string, data: DocumentData): RealizedEntry {
  const text = (field: string): string => {
    const value: unknown = data[field];
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(`รายการ "${id}" มี field "${field}" ที่ไม่ถูกต้อง`);
    }
    return value;
  };
  const decimal = (field: string): string => {
    const value = text(field);
    if (!isDecimalString(value)) {
      throw new Error(`รายการ "${id}" มี field "${field}" ที่ไม่ใช่ตัวเลข`);
    }
    return value;
  };

  const assetType: unknown = data['assetType'];
  if (typeof assetType !== 'string' || !ASSET_TYPES.includes(assetType as AssetType)) {
    throw new Error(`รายการ "${id}" มี assetType ที่ระบบไม่รู้จัก: ${String(assetType)}`);
  }

  const recordedAt =
    (data['recordedAt'] as { toDate?: () => Date } | undefined)?.toDate?.() ?? null;
  const base = {
    id,
    recordedAt,
    platform: text('platform'),
    assetType: assetType as AssetType,
    symbol: text('symbol'),
    displayName: text('displayName'),
    currency: text('currency'),
  };

  const kind: unknown = data['kind'];
  if (kind === 'sale') {
    return {
      ...base,
      kind,
      quantity: decimal('quantity'),
      avgCost: decimal('avgCost'),
      price: decimal('price'),
    };
  }
  if (kind === 'dividend') {
    const note: unknown = data['note'];
    return {
      ...base,
      kind,
      amount: decimal('amount'),
      note: typeof note === 'string' ? note : null,
    };
  }
  throw new Error(`รายการ "${id}" มีประเภทที่ระบบไม่รู้จัก: ${String(kind)}`);
}

/**
 * ใหม่ → เก่า
 *
 * ไม่ใช้ `orderBy('recordedAt')` เพราะ Firestore ตัดเอกสารที่ไม่มี field นั้นทิ้งเงียบ ๆ
 * (เหตุผลเดียวกับ `listHoldings`) — รายการที่ยังไม่ได้ประทับเวลาขึ้นบนสุด เพราะเพิ่งเขียน
 */
export async function listRealized(uid: string): Promise<RealizedEntry[]> {
  const snapshot = await getDocs(realizedCollection(uid));

  return snapshot.docs
    .map((snap) => toRealizedEntry(snap.id, snap.data()))
    .sort(
      (a, b) =>
        (b.recordedAt?.getTime() ?? Number.MAX_SAFE_INTEGER) -
        (a.recordedAt?.getTime() ?? Number.MAX_SAFE_INTEGER),
    );
}

export async function createDividend(uid: string, dividend: ValidatedDividend): Promise<void> {
  await setDoc(doc(realizedCollection(uid)), {
    ...dividend,
    recordedAt: serverTimestamp(),
  });
}

/** ลบแค่รายการ — ไม่คืนจำนวนหน่วยให้ holding เพราะระบบไม่รู้ว่าพอร์ตตอนนี้เป็นอย่างไรแล้ว */
export async function deleteRealized(uid: string, entryId: string): Promise<void> {
  await deleteDoc(doc(realizedCollection(uid), entryId));
}
